/**
 * BoatRenderPass.ts — renders a list of Boats as procedural meshes.
 *
 * Uses a shared mesh (capsule hull + cabin box + mast) and per-boat model
 * matrix. Each boat has its own uniform buffer and bind group; all draw with
 * the same pipeline. Stage 5 will swap the mesh for a glb-loaded model.
 */

import wgsl from "../shaders/boat.wgsl?raw";
import playerWgsl from "../shaders/boat_player.wgsl?raw";
import shadowWgsl from "../shaders/boat_shadow.wgsl?raw";
import {
  createBox,
  createHullLoft,
  mergeMeshes,
} from "../assets/ProceduralAssets";
import { buildParentSailboatHull } from "../sim/hulls/ParentSailboat";
import {
  generateCleats,
  generateCabinTrunk,
  generateCompanionway,
  generateCockpit,
  generateForedeckHatch,
  generateTiller,
  generateOutboardEngine,
  generateCenterboard,
  generateTransom,
} from "../sim/hulls/BoatMeshGenerator";
import type { MeshAsset } from "../assets/MeshAsset";
import { MESH_VERTEX_STRIDE } from "../assets/MeshAsset";
import {
  SCENE_PART_PBR_FLOATS,
  SCENE_PART_PBR_STRIDE,
} from "../assets/GltfLoader";
import {
  createBufferFromTypedArray,
  createUniformBuffer,
} from "../gpu/Buffers";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { ShadowCaster } from "./ShadowPass";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { ShadowUniforms } from "../rendering/ShadowUniforms";
import type { Boat } from "../sim/Boat";
import { DEFAULT_WHEEL_GEAR_RATIO, type BoatHull } from "../sim/BoatHull";

const MODEL_STRUCT_SIZE = 64 * 2; // mat4x4 m + mat4x4 nm

interface BoatGpu {
  uniform: GPUBuffer;
  bg: GPUBindGroup;
  shadowBg?: GPUBindGroup;
  /** Cached cpu staging for the model uniform. */
  cpu: ArrayBuffer;
  f32: Float32Array;
}

interface MeshGroup {
  vbo: GPUBuffer;
  ibo: GPUBuffer;
  indexCount: number;
  /** Optional reduced mesh used only for shadow casting (no thin rigging). */
  shadowVbo?: GPUBuffer;
  shadowIbo?: GPUBuffer;
  shadowIndexCount?: number;
  boatIndices: number[]; // indices into BoatRenderPass.boats[]
}

interface ScenePartGpu {
  /** Index into BoatRenderPass.boats[]. */
  boatIdx: number;
  vbo: GPUBuffer;
  ibo: GPUBuffer;
  indexCount: number;
  uniform: GPUBuffer;
  bg: GPUBindGroup;
  shadowBg?: GPUBindGroup;
  cpu: ArrayBuffer;
  f32: Float32Array;
  /** Pivot translation in body frame (m). */
  pivot: [number, number, number];
  /** Optional group pivot used for parent-style animation. */
  animPivot: [number, number, number];
  /**
   * Animation rotation around the part's local Y axis in radians, computed
   * each frame from the boat state. For helm parts: rudderAngle × gearRatio.
   * For uncoupled outboards: engineAngle × gearRatio.
   */
  animFn: (boat: Boat) => number;
  /** Optional animated local/body-space offset applied after pivot rotation. */
  offsetFn: (boat: Boat) => [number, number, number];
  /** Local axis the animation rotates around (default Y). */
  axis: "X" | "Y" | "Z";
  /** False for tiny dynamic ribbons that should not pollute the shadow map. */
  castsShadow: boolean;
  /** Whether this part is drawn this frame. Defaults to true; fender groups
   *  start hidden and are toggled via {@link BoatRenderPass.setFenderVisibility}. */
  visible: boolean;
  /** Set for parts whose name matches "Fenders.Port" / "Fenders.Starboard".
   *  Lets the app toggle each side on/off independently from the crew
   *  commands UI. */
  fenderSide?: "port" | "starboard";
  /**
   * If set, replaces the standard `boatPose · pivot · animRot` model matrix
   * with a free-floating world-frame pose for this frame. Used by physically
   * simulated parts (hanging fenders) whose pose is decoupled from the boat
   * rigid body but still rendered via this pass for shading consistency.
   * Returns 9 column-major rotation components plus a world translation.
   */
  worldPoseFn?: (boat: Boat) => {
    tx: number;
    ty: number;
    tz: number;
    r: readonly [
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
    ];
  } | null;
  /** Per-frame visibility override (takes precedence over `visible`). */
  visibleFn?: (boat: Boat) => boolean;
  tellTale?: {
    anchorName: string;
    sideSign: -1 | 1;
  };
}

interface TellTaleRuntimeInput {
  windX: number;
  windZ: number;
  time: number;
}

interface TellTalePose {
  apparentBodyX: number;
  apparentBodyZ: number;
  apparentSpeed: number;
  time: number;
}

interface GlbTellTaleMarker {
  name: string;
  position: [number, number, number];
  sideSign: -1 | 1;
}

type Vec3 = [number, number, number];
type Rgb = [number, number, number];

function meshToScenePbr(mesh: MeshAsset): Float32Array {
  const vertexCount = mesh.vertices.length / 9;
  const out = new Float32Array(vertexCount * SCENE_PART_PBR_FLOATS);
  for (let i = 0; i < vertexCount; i++) {
    const src = i * 9;
    const dst = i * SCENE_PART_PBR_FLOATS;
    out[dst] = mesh.vertices[src]!;
    out[dst + 1] = mesh.vertices[src + 1]!;
    out[dst + 2] = mesh.vertices[src + 2]!;
    out[dst + 3] = mesh.vertices[src + 3]!;
    out[dst + 4] = mesh.vertices[src + 4]!;
    out[dst + 5] = mesh.vertices[src + 5]!;
    out[dst + 6] = mesh.vertices[src + 6]!;
    out[dst + 7] = mesh.vertices[src + 7]!;
    out[dst + 8] = mesh.vertices[src + 8]!;
    out[dst + 9] = 0;
    out[dst + 10] = 1;
  }
  return out;
}

function findGlbTellTaleMarkers(
  anchors?: Record<string, [number, number, number]>,
): GlbTellTaleMarker[] {
  if (!anchors) return [];
  const markers: GlbTellTaleMarker[] = [];
  for (const [name, position] of Object.entries(anchors)) {
    const normalized = name.toLowerCase().replace(/[._\-\s]+/g, "");
    if (!normalized.includes("telltale")) continue;
    let sideSign: -1 | 1 | null = null;
    if (normalized.includes("starboard") || normalized.includes("right")) {
      sideSign = 1;
    } else if (normalized.includes("port") || normalized.includes("left")) {
      sideSign = -1;
    } else {
      sideSign = position[2] >= 0 ? 1 : -1;
    }
    markers.push({ name, position, sideSign });
  }
  return markers;
}

function buildTellTaleRibbonMesh(
  sideSign: -1 | 1,
  anchor: Vec3,
  color: Rgb,
  pose?: Partial<TellTalePose>,
  label = "tellTaleRibbon",
): MeshAsset {
  let awX = pose?.apparentBodyX ?? -1;
  let awZ = pose?.apparentBodyZ ?? 0;
  const awMag = Math.hypot(awX, awZ);
  if (awMag < 1e-3) {
    awX = -1;
    awZ = 0;
  } else {
    awX /= awMag;
    awZ /= awMag;
  }

  const apparentSpeed = pose?.apparentSpeed ?? awMag;
  const windTension = Math.min(1, Math.max(0, apparentSpeed / 7));
  const slack = 1 - windTension;
  const flutterAmp = Math.min(0.24, 0.08 + apparentSpeed * 0.015);
  const phase = (pose?.time ?? 0) * (8 + apparentSpeed * 0.6);
  const baseYaw = Math.atan2(awZ - sideSign * 0.08, awX);

  const sampleCount = 15;
  const totalLength = 0.62;
  const rootWidth = 0.042;
  const tailWidth = 0.007;
  const centers: Vec3[] = [];
  const widths: number[] = [];

  let x = anchor[0];
  let z = anchor[2];
  for (let i = 0; i <= sampleCount; i++) {
    const t = i / sampleCount;
    if (i > 0) {
      const tm = (i - 0.5) / sampleCount;
      const yaw =
        baseYaw +
        Math.sin(phase + tm * 7.2 + sideSign * 0.6) *
          flutterAmp *
          (1 - tm * 0.65);
      const stepLen = (totalLength / sampleCount) * (1 - tm * 0.18);
      x += Math.cos(yaw) * stepLen;
      z += Math.sin(yaw) * stepLen;
    }
    const droop = (0.006 * t + 0.09 * t * t) * (0.18 + 0.82 * slack);
    const y = anchor[1] - droop;
    centers.push([x, y, z]);
    widths.push(rootWidth + (tailWidth - rootWidth) * t);
  }

  const verts: number[] = [];
  const idx: number[] = [];

  const pushVertex = (p: Vec3, n: Vec3): number => {
    const i = verts.length / 9;
    verts.push(
      p[0],
      p[1],
      p[2],
      n[0],
      n[1],
      n[2],
      color[0],
      color[1],
      color[2],
    );
    return i;
  };

  const widthAxes: Vec3[] = [];
  const faceNormals: Vec3[] = [];
  for (let i = 0; i <= sampleCount; i++) {
    const prev = centers[Math.max(0, i - 1)]!;
    const next = centers[Math.min(sampleCount, i + 1)]!;
    const tx = next[0] - prev[0];
    const ty = next[1] - prev[1];
    const tz = next[2] - prev[2];
    const tangentLen = Math.hypot(tx, ty, tz) || 1;
    const tangent: Vec3 = [tx / tangentLen, ty / tangentLen, tz / tangentLen];
    const nx = -tz;
    const ny = 0;
    const nz = tx;
    const nm = Math.hypot(nx, ny, nz) || Math.hypot(tx, ty, tz) || 1;
    const sideAxis: Vec3 = [nx / nm, ny / nm, nz / nm];

    const t = i / sampleCount;
    const rollGrow = t * t * (3 - 2 * t);
    const rollAmp = (0.45 + Math.min(1, apparentSpeed / 8) * 0.85) * rollGrow;
    const roll = Math.sin(phase * 1.35 + t * 13.0 + sideSign * 0.9) * rollAmp;
    const rollSin = Math.sin(roll);
    const rollCos = Math.cos(roll);
    const widthAxis: Vec3 = [
      sideAxis[0] * rollSin,
      rollCos,
      sideAxis[2] * rollSin,
    ];
    widthAxes.push(widthAxis);

    const fx = tangent[1] * widthAxis[2] - tangent[2] * widthAxis[1];
    const fy = tangent[2] * widthAxis[0] - tangent[0] * widthAxis[2];
    const fz = tangent[0] * widthAxis[1] - tangent[1] * widthAxis[0];
    const faceLen = Math.hypot(fx, fy, fz) || 1;
    faceNormals.push([fx / faceLen, fy / faceLen, fz / faceLen]);
  }

  const frontLow: number[] = [];
  const frontHigh: number[] = [];
  const backLow: number[] = [];
  const backHigh: number[] = [];
  for (let i = 0; i <= sampleCount; i++) {
    const center = centers[i]!;
    const halfWidth = widths[i]! * 0.5;
    const widthAxis = widthAxes[i]!;
    const n = faceNormals[i]!;
    const low: Vec3 = [
      center[0] - widthAxis[0] * halfWidth,
      center[1] - widthAxis[1] * halfWidth,
      center[2] - widthAxis[2] * halfWidth,
    ];
    const high: Vec3 = [
      center[0] + widthAxis[0] * halfWidth,
      center[1] + widthAxis[1] * halfWidth,
      center[2] + widthAxis[2] * halfWidth,
    ];
    frontLow.push(pushVertex(low, n));
    frontHigh.push(pushVertex(high, n));
    backLow.push(pushVertex(low, [-n[0], -n[1], -n[2]]));
    backHigh.push(pushVertex(high, [-n[0], -n[1], -n[2]]));
  }

  for (let i = 0; i < sampleCount; i++) {
    const a = frontLow[i]!;
    const b = frontLow[i + 1]!;
    const c = frontHigh[i + 1]!;
    const d = frontHigh[i]!;
    idx.push(a, b, c, a, c, d);

    const e = backLow[i]!;
    const f = backHigh[i]!;
    const g = backHigh[i + 1]!;
    const h = backLow[i + 1]!;
    idx.push(e, f, g, e, g, h);
  }

  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

function buildSceneTellTaleRibbon(
  sideSign: -1 | 1,
  pose?: TellTalePose,
): MeshAsset {
  const color: [number, number, number] =
    sideSign > 0 ? [0.05, 1.0, 0.12] : [1.0, 0.05, 0.04];
  return buildTellTaleRibbonMesh(
    sideSign,
    [0.025, -0.02, sideSign * 0.018],
    color,
    pose,
    "glbTellTaleRibbon",
  );
}

export class BoatRenderPass implements RenderPass, ShadowCaster {
  readonly name = "BoatRenderPass";

  private device: GPUDevice;
  private camera: Camera;
  private lighting: LightingUniforms;
  private boats: Boat[];
  private pipeline: GPURenderPipeline;
  /** PBR-ish pipeline (Blinn–Phong + Fresnel) used only for scene-mode
   *  parts (the player boat). Stride 44 (11 f32 per vertex). */
  private playerPipeline: GPURenderPipeline;
  private bgl: GPUBindGroupLayout;
  private gpus: BoatGpu[] = [];
  private meshGroups: MeshGroup[] = [];
  private sceneParts: ScenePartGpu[] = [];
  private shadowPipeline: GPURenderPipeline | null = null;
  /** Shadow pipeline matching the 11-f32 player vertex layout (reads pos only). */
  private playerShadowPipeline: GPURenderPipeline | null = null;
  private shadowBgl: GPUBindGroupLayout | null = null;
  private shadowBuffer: GPUBuffer | null = null;
  /** Set externally to receive shadows (group 1). */
  shadowBindGroup: GPUBindGroup | null = null;
  private tellTalePoseByBoat = new Map<number, TellTalePose>();

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    boats: Boat[],
    shadowConsumerLayout: GPUBindGroupLayout,
    shadow?: ShadowUniforms,
  ) {
    this.device = device;
    this.camera = camera;
    this.lighting = lighting;
    this.boats = boats;
    if (shadow) this.shadowBuffer = shadow.buffer;

    // Group boats by hull preset reference. Each group gets one VBO + IBO.
    // Scene-mode boats (with `customScene`) bypass this and create per-part
    // VBO/IBO in `sceneParts` instead.
    const hullToIndex = new Map<BoatHull, number>();
    for (let i = 0; i < boats.length; i++) {
      const hull = boats[i]!.params;
      if (hull.customScene) {
        // Per-part GPU buffers. Shared per boat instance for now (no inter-boat
        // sharing because the per-part uniforms also encode the boat-world
        // transform, baked each frame). VBO/IBO duplication is fine for the
        // few-meshes-per-boat scale we deal with.
        const scenePartByName = new Map(
          hull.customScene.parts.map((p) => [p.name.toLowerCase(), p]),
        );
        // Mast-attached parts (mast itself + rigging) all rotate around the
        // same pivot: the foot of the mast (tabernacle). Pre-compute it by
        // looking up the `rig.mast` / `mast` root part once.
        const mastRootPart =
          [...scenePartByName.values()].find((p) =>
            /^(rig[._\-]mast|mast)(\b|[._\-\s])/i.test(p.name),
          ) ?? null;
        const mastRootPivot: [number, number, number] | null = mastRootPart
          ? mastRootPart.pivotPosition
          : null;
        // Anything that should rotate with the mast when it folds.
        // Conservative regex — matches rig.mast/mast roots, boom/bom,
        // spreader (salling), stays (sztag/forestay/backstay/shroud),
        // lazyjack and bezier-curve rope splines authored as standing
        // rigging. Lifelines, railings, cleats, capstans, anchor and
        // fenders are deck hardware and stay put.
        const MAST_PART_RE =
          /^(rig[._\-]mast|mast|boom|bom|spreader|salling|sztag|shroud|forestay|backstay|stay|lazyjack|b[eé]ziercurve)(\b|[._\-\s])/i;
        for (const part of hull.customScene.parts) {
          // Skip Empty / pivot-only nodes that have no triangles — they
          // exist as `namedAnchors` for app-level use (camera, fender
          // hangers, etc.) and would otherwise allocate 0-byte GPU buffers.
          if (part.mesh.indices.length === 0) continue;
          // Accept any node whose name *starts with* "wheel" (case-insensitive),
          // covering "wheel", "wheel.L"/"wheel.R", "Wheel", "wheel.001", and
          // "steering_wheel". Previously the regex was anchored, so a single
          // node renamed e.g. "Wheel" or "wheel.001" silently lost animation.
          const isWheel = /wheel/i.test(part.name);
          const tillerMatch = /^(tiller|rumpel)(?:[._\-\s]|$)/i.exec(part.name);
          const isTiller = tillerMatch !== null;
          // Rudder blade: rotates around the vertical rudder stock (Y by
          // default) by exactly `boat.rudderAngle` — no gear, no inversion.
          // Matches the convention "Helm: positive rudderAngle → bow swings
          // to starboard" so the trailing edge goes to starboard too.
          const isRudder = /(^|[._\-\s])rudder/i.test(part.name);
          const isEngine = /^(engine|outboard|motor)(?:[._\-\s]|$)/i.test(
            part.name,
          );
          // Swing-up centerboard (miecz uchylny): pivots around an
          // athwartships axis to retract into the trunk. The GLB authoring
          // convention is that the *rest pose* (no rotation) is the blade
          // fully down (extension=1). Retraction rotates the mesh by up to
          // `centerboardMaxAngleDeg` as `boat.centerboardExtension` goes
          // 1 → 0. Detect any node whose name starts with "centerboard"
          // (case-insensitive), matching the Blender mesh "Centerboard".
          const isCenterboard = /^centerboard/i.test(part.name);
          const isAnchor = /^anchor$/i.test(part.name);
          // Foldable-mast tabernacle: when the boat hull has
          // `foldableMast`, the mast root and everything authored as
          // standing rigging tilt aft together around the mast foot
          // pivot (athwartships axis). Only applies when we found a
          // mast root part to use as the shared pivot.
          // A-frame / gin pole that holds the forestay taut while the
          // mast is being lowered. Authored as a sibling mesh named
          // `Mast.LoweringFrame` with its own pivot at its deck hinge.
          // Rotates with the same angle/direction/speed as the mast,
          // but around its *own* pivot — not the mast tabernacle.
          const isLoweringFrame =
            hull.foldableMast &&
            /(^|[._\-\s])lowering[._\-]?frame(\b|[._\-\s])/i.test(part.name);
          // Boom mesh: rotates with the mast like the rest of the rig
          // group, but is also toggled off as a whole when the crew
          // detaches it from the gooseneck before lowering the spar.
          // Matches nodes whose name starts with `boom` / `bom`.
          const isBoom = /^(boom|bom)(\b|[._\-\s])/i.test(part.name);
          const isMast =
            hull.foldableMast &&
            mastRootPivot !== null &&
            !isLoweringFrame &&
            MAST_PART_RE.test(part.name);
          // Fender groups authored as one mesh per side (Blender nodes
          // `Fenders.Port` / `Fenders.Starboard`). Hidden by default; toggled
          // via state.fendersPortVisible / state.fendersStarboardVisible.
          // Anchor-only naming (BoatLibrary's hideNode) is bypassed for
          // scene mode, so these meshes survive into the parts list.
          const fenderMatch = /^fenders?(?:[._\-\s]+(port|starboard))?$/i.exec(
            part.name,
          );
          const fenderSide: "port" | "starboard" | undefined = fenderMatch
            ? fenderMatch[1]?.toLowerCase() === "port"
              ? "port"
              : "starboard"
            : undefined;
          const wheelGear = hull.wheelGearRatio ?? DEFAULT_WHEEL_GEAR_RATIO;
          const tillerGear = hull.tillerGearRatio ?? 1.0;
          const rudderGear = hull.rudderGearRatio ?? 1.0;
          const engineGear = hull.engineGearRatio ?? 1.0;
          const cbMaxRad =
            ((hull.centerboardMaxAngleDeg ?? 80) * Math.PI) / 180;
          const axisLetter: "X" | "Y" | "Z" = isWheel
            ? (hull.wheelAxis ?? "Y")
            : isTiller
              ? (hull.tillerAxis ?? "Y")
              : isRudder
                ? (hull.rudderAxis ?? "Y")
                : isEngine
                  ? (hull.engineAxis ?? "Y")
                  : isCenterboard
                    ? (hull.centerboardAxis ?? "X")
                    : isAnchor
                      ? "Z"
                      : isMast
                        ? "Z"
                        : "Y";
          const animFn: (b: Boat) => number = isWheel
            ? (b) => b.rudderAngle * wheelGear
            : isTiller
              ? (b) => b.rudderAngle * tillerGear
              : isRudder
                ? (b) => b.rudderAngle * rudderGear
                : isEngine
                  ? (b) =>
                      b.params.engineType === "outboard"
                        ? (b.params.engineSteeringCoupled
                            ? b.rudderAngle
                            : b.engineAngle) * engineGear
                        : 0
                  : isCenterboard
                    ? (b) => (1 - b.centerboardExtension) * cbMaxRad
                    : isAnchor
                      ? (b) => -b.anchorDeployT * Math.PI * 0.65
                      : isMast || isLoweringFrame
                        ? (b) => b.mastAngle
                        : () => 0;
          const offsetFn: (b: Boat) => [number, number, number] = isAnchor
            ? (b) => [0, -Math.max(0, Math.min(1, b.anchorDeployT)) * 2.8, 0]
            : () => [0, 0, 0];
          const tillerRoot = tillerMatch
            ? scenePartByName.get(tillerMatch[1]!.toLowerCase())
            : undefined;
          const animPivot = isTiller
            ? (tillerRoot?.pivotPosition ?? part.pivotPosition)
            : isMast && mastRootPivot
              ? mastRootPivot
              : part.pivotPosition;
          if (
            isWheel ||
            isTiller ||
            isRudder ||
            isEngine ||
            isCenterboard ||
            isAnchor ||
            isMast ||
            isLoweringFrame
          ) {
            const kind = isWheel
              ? "wheel"
              : isTiller
                ? "tiller"
                : isRudder
                  ? "rudder"
                  : isEngine
                    ? "engine"
                    : isCenterboard
                      ? "centerboard"
                      : isAnchor
                        ? "anchor"
                        : isLoweringFrame
                          ? "loweringframe"
                          : "mast";
            const gearOrMax = isCenterboard
              ? `maxDeg=${hull.centerboardMaxAngleDeg ?? 80}`
              : isAnchor
                ? "deploy=anchorDeployT"
                : isLoweringFrame
                  ? `framePivot=(${part.pivotPosition[0].toFixed(2)},${part.pivotPosition[1].toFixed(2)},${part.pivotPosition[2].toFixed(2)})`
                  : isMast
                    ? `mastPivot=(${mastRootPivot![0].toFixed(2)},${mastRootPivot![1].toFixed(2)},${mastRootPivot![2].toFixed(2)})`
                    : isEngine
                      ? `gear=${engineGear} coupled=${hull.engineSteeringCoupled}`
                      : `gear=${isWheel ? wheelGear : isTiller ? tillerGear : rudderGear}`;
            console.log(
              `[BoatRenderPass] boat[${i}] ${kind}="${part.name}" axis=${axisLetter} pivot=(${part.pivotPosition[0].toFixed(3)}, ${part.pivotPosition[1].toFixed(3)}, ${part.pivotPosition[2].toFixed(3)}) ${gearOrMax}`,
            );
          }
          // Pre-warm: built lazily later in postBoatGpuInit, because we
          // need `bgl` and shadow infra. Stash the inputs:
          this.sceneParts.push({
            boatIdx: i,
            vbo: undefined as unknown as GPUBuffer,
            ibo: undefined as unknown as GPUBuffer,
            indexCount: part.mesh.indices.length,
            uniform: undefined as unknown as GPUBuffer,
            bg: undefined as unknown as GPUBindGroup,
            cpu: undefined as unknown as ArrayBuffer,
            f32: undefined as unknown as Float32Array,
            pivot: part.pivotPosition,
            axis: axisLetter,
            animFn,
            offsetFn,
            // Thin rigging / hardware nodes are sub-texel thin at our
            // shadow-map resolution (~0.24 m/texel). Letting them cast
            // produces aliased flickering shadows.
            //
            // Special case `^rig.mast`: a 14-cm Ø mast cylinder is sub-texel,
            // and PCF 5×5 turns a thin caster into 5 parallel banded
            // stripes (one per kernel row). Inflating the shadow-only mesh
            // to >1 PCF kernel width (Ø ~1.7 m) removes the banding but
            // looks like a blob on the ground — worse than no shadow. So
            // we just skip the mast as caster; the mast is clearly visible
            // above water, no shadow is preferable to a fake-thick blob.
            //
            // Skip list: mast, tiller/rudder shafts, lifelines/railings,
            // lazyjack, stays/shrouds, spreaders (salling), bezier-curve
            // ropes, decorative hull-line trim, cleats, capstans, anchor.
            // Kept casters: Hull, cabin, boom, windows, wheel, centerboard,
            // seats, engine.
            castsShadow:
              !/^tiller|^rudder|^rig\.mast|lifelines?|relling|lazyjack|sztag|shroud|forestay|backstay|stay\b|salling|b[eé]ziercurve|hulllines?|cleat|capstan|anchor/i.test(
                part.name,
              ),
            visible: fenderSide === undefined,
            fenderSide,
            visibleFn: isBoom ? (b: Boat) => !b.boomDetached : undefined,
            animPivot,
            // Stash mesh data on the entry so we can build buffers below.
            // Using plain assignment to a private "_seed" field via cast —
            // we'll consume + delete it right after gpu init.
            _seedVerts: part.verticesPbr,
            _seedIdx: part.mesh.indices,
            _label: `${i}.${part.name}`,
          } as unknown as ScenePartGpu);
        }
        for (const marker of findGlbTellTaleMarkers(hull.namedAnchors)) {
          const mesh = buildSceneTellTaleRibbon(marker.sideSign);
          this.sceneParts.push({
            boatIdx: i,
            vbo: undefined as unknown as GPUBuffer,
            ibo: undefined as unknown as GPUBuffer,
            indexCount: mesh.indices.length,
            uniform: undefined as unknown as GPUBuffer,
            bg: undefined as unknown as GPUBindGroup,
            cpu: undefined as unknown as ArrayBuffer,
            f32: undefined as unknown as Float32Array,
            pivot: marker.position,
            animPivot: marker.position,
            axis: "Y",
            animFn: () => 0,
            offsetFn: () => [0, 0, 0],
            castsShadow: false,
            visible: true,
            tellTale: {
              anchorName: marker.name,
              sideSign: marker.sideSign,
            },
            _seedVerts: meshToScenePbr(mesh),
            _seedIdx: mesh.indices,
            _label: `${i}.${marker.name}`,
          } as unknown as ScenePartGpu);
        }
        continue; // skip standard meshGroup path for this boat
      }
      let gIdx = hullToIndex.get(hull);
      if (gIdx === undefined) {
        const built =
          hull.customMesh != null
            ? { mesh: hull.customMesh, shadowMesh: undefined }
            : buildBoatMesh(hull, {
                centerboardExtension: boats[i]!.centerboardExtension,
              });
        const mesh = built.mesh;
        const vbo = createBufferFromTypedArray(
          device,
          mesh.vertices,
          GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
          `BoatRenderPass.vbo[${this.meshGroups.length}]`,
        );
        const ibo = createBufferFromTypedArray(
          device,
          mesh.indices,
          GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
          `BoatRenderPass.ibo[${this.meshGroups.length}]`,
        );
        let shadowVbo: GPUBuffer | undefined;
        let shadowIbo: GPUBuffer | undefined;
        let shadowIndexCount: number | undefined;
        if (built.shadowMesh) {
          shadowVbo = createBufferFromTypedArray(
            device,
            built.shadowMesh.vertices,
            GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
            `BoatRenderPass.shadowVbo[${this.meshGroups.length}]`,
          );
          shadowIbo = createBufferFromTypedArray(
            device,
            built.shadowMesh.indices,
            GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
            `BoatRenderPass.shadowIbo[${this.meshGroups.length}]`,
          );
          shadowIndexCount = built.shadowMesh.indices.length;
        }
        gIdx = this.meshGroups.length;
        this.meshGroups.push({
          vbo,
          ibo,
          indexCount: mesh.indices.length,
          shadowVbo,
          shadowIbo,
          shadowIndexCount,
          boatIndices: [],
        });
        hullToIndex.set(hull, gIdx);
      }
      this.meshGroups[gIdx]!.boatIndices.push(i);
    }
    // Use the first hull's stride as the canonical layout (all share 9 f32).
    const sharedStride = MESH_VERTEX_STRIDE;

    const module = device.createShaderModule({
      label: "boat.wgsl",
      code: wgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "BoatRenderPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: "uniform" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "BoatRenderPass.pipeline",
      layout: device.createPipelineLayout({
        bindGroupLayouts: [this.bgl, shadowConsumerLayout],
      }),
      vertex: {
        module,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: sharedStride,
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x3" },
              { shaderLocation: 2, offset: 24, format: "float32x3" },
            ],
          },
        ],
      },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [{ format: rt.current.colorFormat }],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: true,
        depthCompare: REVERSE_Z_COMPARE,
      },
    });

    // --- Player (scene-parts) pipeline: 11-f32 vertex, Blinn–Phong + Fresnel
    const playerModule = device.createShaderModule({
      label: "boat_player.wgsl",
      code: playerWgsl,
    });
    this.playerPipeline = device.createRenderPipeline({
      label: "BoatRenderPass.playerPipeline",
      layout: device.createPipelineLayout({
        bindGroupLayouts: [this.bgl, shadowConsumerLayout],
      }),
      vertex: {
        module: playerModule,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: SCENE_PART_PBR_STRIDE,
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x3" },
              { shaderLocation: 2, offset: 24, format: "float32x3" },
              { shaderLocation: 3, offset: 36, format: "float32" },
              { shaderLocation: 4, offset: 40, format: "float32" },
            ],
          },
        ],
      },
      fragment: {
        module: playerModule,
        entryPoint: "fs_main",
        targets: [{ format: rt.current.colorFormat }],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: true,
        depthCompare: REVERSE_Z_COMPARE,
      },
    });

    for (const _ of boats) this.gpus.push(this.makeBoatGpu());

    // --- Optional shadow caster pipeline -----------------------------------
    if (this.shadowBuffer) {
      this.shadowBgl = device.createBindGroupLayout({
        label: "BoatRenderPass.shadow.bgl",
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.VERTEX,
            buffer: { type: "uniform" },
          },
          {
            binding: 1,
            visibility: GPUShaderStage.VERTEX,
            buffer: { type: "uniform" },
          },
        ],
      });
      const shadowModule = device.createShaderModule({
        label: "boat_shadow.wgsl",
        code: shadowWgsl,
      });
      this.shadowPipeline = device.createRenderPipeline({
        label: "BoatRenderPass.shadow.pipeline",
        layout: device.createPipelineLayout({
          bindGroupLayouts: [this.shadowBgl],
        }),
        vertex: {
          module: shadowModule,
          entryPoint: "vs_main",
          buffers: [
            {
              arrayStride: sharedStride,
              attributes: [
                { shaderLocation: 0, offset: 0, format: "float32x3" },
                { shaderLocation: 1, offset: 12, format: "float32x3" },
                { shaderLocation: 2, offset: 24, format: "float32x3" },
              ],
            },
          ],
        },
        primitive: { topology: "triangle-list", cullMode: "back" },
        depthStencil: {
          format: "depth32float",
          depthWriteEnabled: true,
          depthCompare: REVERSE_Z_COMPARE,
          // Reverse-Z depth bias to kill diagonal shadow-acne stripes
          // on flat boat surfaces (deck, cabin top) at low sun angles.
          // Same values as terrain ShadowPass + StaticMeshPass shadow.
          depthBias: -2,
          depthBiasSlopeScale: -2.5,
          depthBiasClamp: -0.001,
        },
      });
      // Player shadow pipeline: identical to shadowPipeline but with stride
      // 44 to match scene-part VBOs. The shadow shader reads only @location(0).
      this.playerShadowPipeline = device.createRenderPipeline({
        label: "BoatRenderPass.playerShadow.pipeline",
        layout: device.createPipelineLayout({
          bindGroupLayouts: [this.shadowBgl],
        }),
        vertex: {
          module: shadowModule,
          entryPoint: "vs_main",
          buffers: [
            {
              arrayStride: SCENE_PART_PBR_STRIDE,
              attributes: [
                { shaderLocation: 0, offset: 0, format: "float32x3" },
                { shaderLocation: 1, offset: 12, format: "float32x3" },
                { shaderLocation: 2, offset: 24, format: "float32x3" },
              ],
            },
          ],
        },
        primitive: { topology: "triangle-list", cullMode: "back" },
        depthStencil: {
          format: "depth32float",
          depthWriteEnabled: true,
          depthCompare: REVERSE_Z_COMPARE,
          // Reverse-Z negative bias: pushes the caster geometry slightly
          // *away* from the sun in the shadow map, so the boat's own
          // surfaces don't self-shadow. Matches the procedural
          // `shadowPipeline` config above. Combined with receiver-side
          // `shadow.depthBias` (3e-5) this gives clean shadows on both
          // the boat itself and on water.
          depthBias: -2,
          depthBiasSlopeScale: -2.5,
          depthBiasClamp: -0.001,
        },
      });
      // Build per-boat shadow bind groups.
      for (let i = 0; i < this.gpus.length; i++) {
        const g = this.gpus[i]!;
        g.shadowBg = device.createBindGroup({
          label: `BoatRenderPass.shadow.bg[${i}]`,
          layout: this.shadowBgl,
          entries: [
            { binding: 0, resource: { buffer: this.shadowBuffer } },
            { binding: 1, resource: { buffer: g.uniform } },
          ],
        });
      }
    }

    // ---- Finalise scene-part GPU resources --------------------------------
    for (const sp of this.sceneParts) {
      type Seed = ScenePartGpu & {
        _seedVerts: Float32Array;
        _seedIdx: Uint32Array;
        _label: string;
      };
      const seed = sp as Seed;
      sp.vbo = createBufferFromTypedArray(
        device,
        seed._seedVerts,
        GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
        `BoatRenderPass.scenePart.vbo[${seed._label}]`,
      );
      sp.ibo = createBufferFromTypedArray(
        device,
        seed._seedIdx,
        GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
        `BoatRenderPass.scenePart.ibo[${seed._label}]`,
      );
      const partUniform = createUniformBuffer(
        device,
        MODEL_STRUCT_SIZE,
        `BoatRenderPass.scenePart.uniform[${seed._label}]`,
      );
      sp.uniform = partUniform;
      sp.cpu = new ArrayBuffer(MODEL_STRUCT_SIZE);
      sp.f32 = new Float32Array(sp.cpu);
      sp.bg = device.createBindGroup({
        label: `BoatRenderPass.scenePart.bg[${seed._label}]`,
        layout: this.bgl,
        entries: [
          { binding: 0, resource: { buffer: this.camera.buffer } },
          { binding: 1, resource: { buffer: this.lighting.buffer } },
          { binding: 2, resource: { buffer: partUniform } },
        ],
      });
      if (this.shadowBuffer && this.shadowBgl) {
        sp.shadowBg = device.createBindGroup({
          label: `BoatRenderPass.scenePart.shadow.bg[${seed._label}]`,
          layout: this.shadowBgl,
          entries: [
            { binding: 0, resource: { buffer: this.shadowBuffer } },
            { binding: 1, resource: { buffer: partUniform } },
          ],
        });
      }
      // Free seed mesh bytes.
      delete (seed as unknown as { _seedVerts?: Float32Array })._seedVerts;
      delete (seed as unknown as { _seedIdx?: Uint32Array })._seedIdx;
    }
  }

  private makeBoatGpu(): BoatGpu {
    const uniform = createUniformBuffer(
      this.device,
      MODEL_STRUCT_SIZE,
      "BoatRenderPass.model",
    );
    const cpu = new ArrayBuffer(MODEL_STRUCT_SIZE);
    const bg = this.device.createBindGroup({
      label: "BoatRenderPass.bg",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.camera.buffer } },
        { binding: 1, resource: { buffer: this.lighting.buffer } },
        { binding: 2, resource: { buffer: uniform } },
      ],
    });
    return { uniform, bg, cpu, f32: new Float32Array(cpu) };
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    // Update model uniforms for each boat.
    for (let i = 0; i < this.boats.length; i++) {
      const b = this.boats[i]!;
      const g = this.gpus[i]!;
      // Body axes in world after yaw·pitch·roll:
      //   bX = ( cosP·sh,            sinP, -cosP·ch                  )
      //   bY = (-sinP·cosR·sh + sinR·ch,  cosP·cosR,  sinP·cosR·ch + sinR·sh )
      //   bZ = ( sinP·sinR·sh + cosR·ch, -cosP·sinR, -sinP·sinR·ch + cosR·sh )
      // (yaw uses our convention: body +X → world (sh, 0, -ch).)
      const ch = Math.cos(b.heading);
      const sh = Math.sin(b.heading);
      const cp = Math.cos(b.pitch);
      const sp = Math.sin(b.pitch);
      const cr = Math.cos(b.roll);
      const sr = Math.sin(b.roll);
      const bxX = cp * sh;
      const bxY = sp;
      const bxZ = -cp * ch;
      const byX = -sp * cr * sh + sr * ch;
      const byY = cp * cr;
      const byZ = sp * cr * ch + sr * sh;
      const bzX = sp * sr * sh + cr * ch;
      const bzY = -cp * sr;
      const bzZ = -sp * sr * ch + cr * sh;
      const f = g.f32;
      // m: column-major mat4x4
      f[0] = bxX;
      f[1] = bxY;
      f[2] = bxZ;
      f[3] = 0;
      f[4] = byX;
      f[5] = byY;
      f[6] = byZ;
      f[7] = 0;
      f[8] = bzX;
      f[9] = bzY;
      f[10] = bzZ;
      f[11] = 0;
      f[12] = b.posX;
      f[13] = b.posY;
      f[14] = b.posZ;
      f[15] = 1;
      // nm: same upper 3x3 (pure rotation), embedded in mat4.
      f[16] = bxX;
      f[17] = bxY;
      f[18] = bxZ;
      f[19] = 0;
      f[20] = byX;
      f[21] = byY;
      f[22] = byZ;
      f[23] = 0;
      f[24] = bzX;
      f[25] = bzY;
      f[26] = bzZ;
      f[27] = 0;
      f[28] = 0;
      f[29] = 0;
      f[30] = 0;
      f[31] = 1;
      this.device.queue.writeBuffer(g.uniform, 0, g.cpu);
    }

    // ---- Update scene-part uniforms ---------------------------------------
    // For each part: finalModelMatrix = boatModel · pivotTranslation · animRot.
    // Where animRot rotates around the part's local axis (Y by default for
    // steering wheels). Both translation and rotation are baked together.
    for (const sp of this.sceneParts) {
      const g = this.gpus[sp.boatIdx]!;
      const b = this.boats[sp.boatIdx]!;
      // World-frame override: physically simulated parts (hanging fenders)
      // ignore the boat-relative pivot chain and write their world pose
      // directly. The boat's normal matrix is still column-major rotation,
      // so we just splat the provided 3x3 + translation here.
      if (sp.worldPoseFn) {
        const wp = sp.worldPoseFn(b);
        if (wp) {
          const f = sp.f32;
          const r = wp.r;
          f[0] = r[0];
          f[1] = r[1];
          f[2] = r[2];
          f[3] = 0;
          f[4] = r[3];
          f[5] = r[4];
          f[6] = r[5];
          f[7] = 0;
          f[8] = r[6];
          f[9] = r[7];
          f[10] = r[8];
          f[11] = 0;
          f[12] = wp.tx;
          f[13] = wp.ty;
          f[14] = wp.tz;
          f[15] = 1;
          f[16] = r[0];
          f[17] = r[1];
          f[18] = r[2];
          f[19] = 0;
          f[20] = r[3];
          f[21] = r[4];
          f[22] = r[5];
          f[23] = 0;
          f[24] = r[6];
          f[25] = r[7];
          f[26] = r[8];
          f[27] = 0;
          f[28] = 0;
          f[29] = 0;
          f[30] = 0;
          f[31] = 1;
          this.device.queue.writeBuffer(sp.uniform, 0, sp.cpu);
        }
        continue;
      }
      const M = g.f32; // 32 floats, mat4 (0..15) and nm (16..31)
      // Animation angle in radians around local axis.
      const a = sp.animFn(b);
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      // 3x3 rotation around chosen axis (column-major).
      let r00 = 1,
        r01 = 0,
        r02 = 0;
      let r10 = 0,
        r11 = 1,
        r12 = 0;
      let r20 = 0,
        r21 = 0,
        r22 = 1;
      if (sp.axis === "Y") {
        r00 = ca;
        r02 = sa;
        r20 = -sa;
        r22 = ca;
      } else if (sp.axis === "X") {
        r11 = ca;
        r12 = -sa;
        r21 = sa;
        r22 = ca;
      } else {
        r00 = ca;
        r01 = -sa;
        r10 = sa;
        r11 = ca;
      }
      // Boat 3x3 rotation (from the just-written boat uniform).
      const m00 = M[0]!,
        m01 = M[4]!,
        m02 = M[8]!;
      const m10 = M[1]!,
        m11 = M[5]!,
        m12 = M[9]!;
      const m20 = M[2]!,
        m21 = M[6]!,
        m22 = M[10]!;
      // Combined 3x3 = boat * partRot.
      const c00 = m00 * r00 + m01 * r10 + m02 * r20;
      const c01 = m00 * r01 + m01 * r11 + m02 * r21;
      const c02 = m00 * r02 + m01 * r12 + m02 * r22;
      const c10 = m10 * r00 + m11 * r10 + m12 * r20;
      const c11 = m10 * r01 + m11 * r11 + m12 * r21;
      const c12 = m10 * r02 + m11 * r12 + m12 * r22;
      const c20 = m20 * r00 + m21 * r10 + m22 * r20;
      const c21 = m20 * r01 + m21 * r11 + m22 * r21;
      const c22 = m20 * r02 + m21 * r12 + m22 * r22;
      // Pivot translation in body space → world. For grouped parts (for
      // example Tiller children authored under a Blender parent), rotate the
      // child pivot around `animPivot` so the hierarchy behaves like one unit.
      const px = sp.pivot[0],
        py = sp.pivot[1],
        pz = sp.pivot[2];
      const apx = sp.animPivot[0],
        apy = sp.animPivot[1],
        apz = sp.animPivot[2];
      const dpx = px - apx,
        dpy = py - apy,
        dpz = pz - apz;
      const rpx = r00 * dpx + r01 * dpy + r02 * dpz;
      const rpy = r10 * dpx + r11 * dpy + r12 * dpz;
      const rpz = r20 * dpx + r21 * dpy + r22 * dpz;
      const bodyPx = apx + rpx;
      const bodyPy = apy + rpy;
      const bodyPz = apz + rpz;
      const [ox, oy, oz] = sp.offsetFn(b);
      const wpx = m00 * bodyPx + m01 * bodyPy + m02 * bodyPz;
      const wpy = m10 * bodyPx + m11 * bodyPy + m12 * bodyPz;
      const wpz = m20 * bodyPx + m21 * bodyPy + m22 * bodyPz;
      const wox = m00 * ox + m01 * oy + m02 * oz;
      const woy = m10 * ox + m11 * oy + m12 * oz;
      const woz = m20 * ox + m21 * oy + m22 * oz;
      // Translation = boatPos + boatRot * pivot.
      const tx = b.posX + wpx + wox;
      const ty = b.posY + wpy + woy;
      const tz = b.posZ + wpz + woz;

      const f = sp.f32;
      f[0] = c00;
      f[1] = c10;
      f[2] = c20;
      f[3] = 0;
      f[4] = c01;
      f[5] = c11;
      f[6] = c21;
      f[7] = 0;
      f[8] = c02;
      f[9] = c12;
      f[10] = c22;
      f[11] = 0;
      f[12] = tx;
      f[13] = ty;
      f[14] = tz;
      f[15] = 1;
      // Normal matrix (same rotation, since no non-uniform scale).
      f[16] = c00;
      f[17] = c10;
      f[18] = c20;
      f[19] = 0;
      f[20] = c01;
      f[21] = c11;
      f[22] = c21;
      f[23] = 0;
      f[24] = c02;
      f[25] = c12;
      f[26] = c22;
      f[27] = 0;
      f[28] = 0;
      f[29] = 0;
      f[30] = 0;
      f[31] = 1;
      this.device.queue.writeBuffer(sp.uniform, 0, sp.cpu);
    }

    const pass = encoder.beginRenderPass({
      label: "BoatRenderPass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: frame.renderTarget.depthView,
        depthLoadOp: "load",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    if (this.shadowBindGroup) {
      pass.setBindGroup(1, this.shadowBindGroup);
    }
    for (const group of this.meshGroups) {
      pass.setVertexBuffer(0, group.vbo);
      pass.setIndexBuffer(group.ibo, "uint32");
      for (const idx of group.boatIndices) {
        pass.setBindGroup(0, this.gpus[idx]!.bg);
        pass.drawIndexed(group.indexCount, 1, 0, 0, 0);
      }
    }
    // Scene-mode boats: draw each part with its own VBO/IBO and uniform.
    // Uses the PBR-ish player pipeline (Blinn–Phong + Fresnel) and a
    // 11-f32 vertex layout. Shadow bind group (group 1) carries over from
    // the main pipeline above since both pipelines share the layout.
    if (this.sceneParts.length > 0) {
      pass.setPipeline(this.playerPipeline);
      if (this.shadowBindGroup) {
        pass.setBindGroup(1, this.shadowBindGroup);
      }
      for (const sp of this.sceneParts) {
        const vis = sp.visibleFn
          ? sp.visibleFn(this.boats[sp.boatIdx]!)
          : sp.visible;
        if (!vis) continue;
        pass.setVertexBuffer(0, sp.vbo);
        pass.setIndexBuffer(sp.ibo, "uint32");
        pass.setBindGroup(0, sp.bg);
        pass.drawIndexed(sp.indexCount, 1, 0, 0, 0);
      }
    }
    pass.end();
  }

  private computeTellTalePose(
    boatIdx: number,
    rt: TellTaleRuntimeInput,
  ): TellTalePose {
    const b = this.boats[boatIdx]!;
    const ch = Math.cos(b.heading);
    const sh = Math.sin(b.heading);
    const fwdX = sh;
    const fwdZ = -ch;
    const rgtX = ch;
    const rgtZ = sh;
    const worldVx = b.surge * fwdX + b.sway * rgtX;
    const worldVz = b.surge * fwdZ + b.sway * rgtZ;
    const appX = rt.windX - worldVx;
    const appZ = rt.windZ - worldVz;
    const appBodyX = appX * fwdX + appZ * fwdZ;
    const appBodyZ = appX * rgtX + appZ * rgtZ;
    return {
      apparentBodyX: appBodyX,
      apparentBodyZ: appBodyZ,
      apparentSpeed: Math.hypot(appBodyX, appBodyZ),
      time: rt.time,
    };
  }

  private shouldRefreshTellTales(boatIdx: number, next: TellTalePose): boolean {
    const prev = this.tellTalePoseByBoat.get(boatIdx);
    if (!prev) return true;
    const dt = next.time - prev.time;
    if (dt >= 1 / 20) return true; // 20 Hz flutter update
    const angNext = Math.atan2(next.apparentBodyZ, next.apparentBodyX);
    const angPrev = Math.atan2(prev.apparentBodyZ, prev.apparentBodyX);
    const dAng = Math.atan2(
      Math.sin(angNext - angPrev),
      Math.cos(angNext - angPrev),
    );
    if (Math.abs(dAng) > 0.06) return true;
    if (Math.abs(next.apparentSpeed - prev.apparentSpeed) > 0.3) return true;
    return false;
  }

  private updateSceneTellTalesForBoat(
    boatIdx: number,
    pose: TellTalePose,
  ): boolean {
    let updated = false;
    for (const sp of this.sceneParts) {
      if (sp.boatIdx !== boatIdx || !sp.tellTale) continue;
      const mesh = buildSceneTellTaleRibbon(sp.tellTale.sideSign, pose);
      const verts = meshToScenePbr(mesh);
      this.device.queue.writeBuffer(
        sp.vbo,
        0,
        verts.buffer,
        verts.byteOffset,
        verts.byteLength,
      );
      updated = true;
    }
    return updated;
  }

  encodeShadow(pass: GPURenderPassEncoder): void {
    if (!this.shadowPipeline) return;
    pass.setPipeline(this.shadowPipeline);
    for (const group of this.meshGroups) {
      // Prefer a reduced shadow mesh when available (procedural hulls strip
      // thin rigging — stays/stanchions/rails — to avoid shadow-map
      // flicker on cables that are sub-texel thick at our 0.24 m/texel
      // resolution). Custom-loaded meshes fall back to the full mesh.
      const vbo = group.shadowVbo ?? group.vbo;
      const ibo = group.shadowIbo ?? group.ibo;
      const count = group.shadowIndexCount ?? group.indexCount;
      pass.setVertexBuffer(0, vbo);
      pass.setIndexBuffer(ibo, "uint32");
      for (const idx of group.boatIndices) {
        const sg = this.gpus[idx]!.shadowBg;
        if (!sg) continue;
        pass.setBindGroup(0, sg);
        pass.drawIndexed(count, 1, 0, 0, 0);
      }
    }
    if (this.sceneParts.length > 0 && this.playerShadowPipeline) {
      pass.setPipeline(this.playerShadowPipeline);
      for (const sp of this.sceneParts) {
        if (!sp.shadowBg || !sp.castsShadow) continue;
        const vis = sp.visibleFn
          ? sp.visibleFn(this.boats[sp.boatIdx]!)
          : sp.visible;
        if (!vis) continue;
        pass.setVertexBuffer(0, sp.vbo);
        pass.setIndexBuffer(sp.ibo, "uint32");
        pass.setBindGroup(0, sp.shadowBg);
        pass.drawIndexed(sp.indexCount, 1, 0, 0, 0);
      }
    }
  }

  /**
   * Rebuild the vertex + index buffers for the mesh group that contains the
   * given boat index.  Call this after mutating `boats[boatIdx].params` (and
   * re-deriving the hull) so the GPU mesh stays in sync with the new spec.
   */
  rebuildMeshForBoat(boatIdx: number): void {
    this.rebuildMeshForBoatRuntime(boatIdx);
  }

  /**
   * Toggle visibility of the per-side GLB fender groups for `boatIdx`. The
   * GLB authoring convention is one mesh per side named `Fenders.Port` /
   * `Fenders.Starboard`.
   * Both sides default hidden at boat construction.
   */
  setFenderVisibility(
    boatIdx: number,
    port: boolean,
    starboard: boolean,
  ): void {
    for (const sp of this.sceneParts) {
      if (sp.boatIdx !== boatIdx || !sp.fenderSide) continue;
      sp.visible = sp.fenderSide === "port" ? port : starboard;
    }
  }

  /**
   * Register a free-floating scene part rendered in world-frame coords each
   * frame via {@link worldPoseFn}. Used for physically simulated objects
   * (hanging fenders) that share the player boat's shading pipeline but
   * have a pose decoupled from the boat rigid body. GPU resources are
   * allocated synchronously; safe to call after {@link postBoatGpuInit}.
   *
   * @param boatIdx index into `boats[]` (currently used only for source-of-truth
   *        when sampling boat state inside `worldPoseFn`)
   * @param verticesPbr 11-f32 PBR vertex stream (pos/nrm/col/met/rgh)
   * @param indices uint32 triangle indices
   * @param worldPoseFn returns world translation + 3x3 rotation each frame
   * @param visibleFn returns whether this part should be drawn this frame
   * @param label diagnostic label (e.g. "boat0.fender.bow.port")
   */
  addFloatingScenePart(
    boatIdx: number,
    verticesPbr: Float32Array,
    indices: Uint32Array,
    worldPoseFn: NonNullable<ScenePartGpu["worldPoseFn"]>,
    visibleFn: (boat: Boat) => boolean,
    label: string,
  ): void {
    const device = this.device;
    const vbo = createBufferFromTypedArray(
      device,
      verticesPbr,
      GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      `BoatRenderPass.float.vbo[${label}]`,
    );
    const ibo = createBufferFromTypedArray(
      device,
      indices,
      GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
      `BoatRenderPass.float.ibo[${label}]`,
    );
    const uniform = createUniformBuffer(
      device,
      MODEL_STRUCT_SIZE,
      `BoatRenderPass.float.uniform[${label}]`,
    );
    const cpu = new ArrayBuffer(MODEL_STRUCT_SIZE);
    const bg = device.createBindGroup({
      label: `BoatRenderPass.float.bg[${label}]`,
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.camera.buffer } },
        { binding: 1, resource: { buffer: this.lighting.buffer } },
        { binding: 2, resource: { buffer: uniform } },
      ],
    });
    let shadowBg: GPUBindGroup | undefined;
    if (this.shadowBuffer && this.shadowBgl) {
      shadowBg = device.createBindGroup({
        label: `BoatRenderPass.float.shadow.bg[${label}]`,
        layout: this.shadowBgl,
        entries: [
          { binding: 0, resource: { buffer: this.shadowBuffer } },
          { binding: 1, resource: { buffer: uniform } },
        ],
      });
    }
    this.sceneParts.push({
      boatIdx,
      vbo,
      ibo,
      indexCount: indices.length,
      uniform,
      bg,
      shadowBg,
      cpu,
      f32: new Float32Array(cpu),
      pivot: [0, 0, 0],
      animPivot: [0, 0, 0],
      axis: "Y",
      animFn: () => 0,
      offsetFn: () => [0, 0, 0],
      // Floating parts (hanging fenders) are small, dynamic and decorative.
      // Casting their shadow into the 0.24 m/texel shadow map produces
      // flickering sub-texel stripes that follow the player around.
      castsShadow: false,
      visible: true,
      worldPoseFn,
      visibleFn,
    } as ScenePartGpu);
  }

  rebuildMeshForBoatRuntime(boatIdx: number, rt?: TellTaleRuntimeInput): void {
    let pose: TellTalePose | undefined;
    if (rt) {
      const hull = this.boats[boatIdx]!.params;
      const rigEnabled = hull.spec.showRigging !== false;
      if (hull.spec.type === "sailboat" && rigEnabled) {
        pose = this.computeTellTalePose(boatIdx, rt);
        if (!this.shouldRefreshTellTales(boatIdx, pose)) return;
      }
    }
    const updatedSceneTellTales = pose
      ? this.updateSceneTellTalesForBoat(boatIdx, pose)
      : false;
    for (const group of this.meshGroups) {
      if (!group.boatIndices.includes(boatIdx)) continue;
      const hull = this.boats[boatIdx]!.params;
      const built =
        hull.customMesh != null
          ? { mesh: hull.customMesh, shadowMesh: undefined }
          : buildBoatMesh(hull, {
              apparentBodyX: pose?.apparentBodyX,
              apparentBodyZ: pose?.apparentBodyZ,
              apparentSpeed: pose?.apparentSpeed,
              time: pose?.time,
              centerboardExtension: this.boats[boatIdx]!.centerboardExtension,
            });
      const mesh = built.mesh;
      group.vbo.destroy();
      group.ibo.destroy();
      group.vbo = createBufferFromTypedArray(
        this.device,
        mesh.vertices,
        GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
        `BoatRenderPass.vbo[${boatIdx}]`,
      );
      group.ibo = createBufferFromTypedArray(
        this.device,
        mesh.indices,
        GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
        `BoatRenderPass.ibo[${boatIdx}]`,
      );
      group.indexCount = mesh.indices.length;
      if (built.shadowMesh) {
        group.shadowVbo?.destroy();
        group.shadowIbo?.destroy();
        group.shadowVbo = createBufferFromTypedArray(
          this.device,
          built.shadowMesh.vertices,
          GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
          `BoatRenderPass.shadowVbo[${boatIdx}]`,
        );
        group.shadowIbo = createBufferFromTypedArray(
          this.device,
          built.shadowMesh.indices,
          GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
          `BoatRenderPass.shadowIbo[${boatIdx}]`,
        );
        group.shadowIndexCount = built.shadowMesh.indices.length;
      }
      if (pose) this.tellTalePoseByBoat.set(boatIdx, pose);
      break;
    }
    if (pose && updatedSceneTellTales)
      this.tellTalePoseByBoat.set(boatIdx, pose);
  }

  destroy(): void {
    for (const g of this.meshGroups) {
      g.vbo.destroy();
      g.ibo.destroy();
      g.shadowVbo?.destroy();
      g.shadowIbo?.destroy();
    }
    for (const g of this.gpus) g.uniform.destroy();
    for (const sp of this.sceneParts) {
      sp.vbo.destroy();
      sp.ibo.destroy();
      sp.uniform.destroy();
    }
  }
}

function buildBoatMesh(
  hull: BoatHull,
  runtime: {
    apparentBodyX?: number;
    apparentBodyZ?: number;
    apparentSpeed?: number;
    time?: number;
    centerboardExtension?: number;
  } = {},
): { mesh: MeshAsset; shadowMesh: MeshAsset } {
  // Lofted hull replaces the legacy capsule. Body Y=0 = waterline.
  // Hull bottom at -draftEq, deck top at +freeboard.
  const draft = hull.draftEq;
  const freeboard = hull.freeboard;
  const hullMesh =
    hull.spec.type === "sailboat"
      ? buildParentSailboatHull(
          {
            LOA: hull.length,
            BWL: hull.beam,
            Tc: draft,
            freeboard,
            hullColor: hull.hullColor,
            deckColor: hull.deckColor,
            style: hull.spec.hullStyle,
            // Cove stripe: dark accent band for modern-chine (Antila-style),
            // subtle near-WL stripe for classic keelers.
            coveStripeColor:
              hull.spec.hullStyle === "modern-chine"
                ? [0.12, 0.14, 0.18]
                : [0.08, 0.1, 0.12],
          },
          "hull",
        )
      : createHullLoft(
          {
            type: hull.spec.type,
            length: hull.length,
            beam: hull.beam,
            draft,
            freeboard,
            color: hull.hullColor,
            deckColor: hull.deckColor,
          },
          "hull",
        );

  const showSuperstructure = hull.spec.showSuperstructure !== false;
  const showRigging = hull.spec.showRigging !== false;

  // Each part can opt out of the shadow caster (`shadowSkip: true`). Thin
  // rigging — stays, shrouds, stanchions, lifelines, telltales — is at or
  // below the 0.24 m/texel resolution of the shadow map; rendering it makes
  // the on-water shadow flicker and produces 4 sub-pixel parallel lines that
  // look like artefacts. Hull, cabin, mast and boom remain casters.
  const parts: {
    mesh: MeshAsset;
    transform?: (p: [number, number, number]) => [number, number, number];
    shadowSkip?: boolean;
  }[] = [{ mesh: hullMesh }];

  if (hull.cabin && showSuperstructure) {
    const c = hull.cabin;
    if (hull.spec.type === "sailboat") {
      // Antila-style raked-front trunk with tumblehome sides.
      for (const p of generateCabinTrunk(c, freeboard)) parts.push(p);
      for (const p of generateCompanionway(c, freeboard)) parts.push(p);
      for (const p of generateCockpit(c, hull.length, hull.beam, freeboard))
        parts.push(p);
      for (const p of generateForedeckHatch(c, freeboard)) parts.push(p);
    } else {
      // Houseboats keep the simple box.
      const cabinCenterY = freeboard + c.height * 0.5;
      const cabinMesh = createBox(
        c.length,
        c.height,
        c.width,
        c.color,
        "cabin",
      );
      parts.push({
        mesh: cabinMesh,
        transform: ([x, y, z]) => [x + c.offsetX, y + cabinCenterY, z],
      });
    }
  }

  // Mast: stands on deck.
  if (showRigging) {
    const m = hull.mast;
    const mastMesh = createBox(
      m.radius * 2,
      m.height,
      m.radius * 2,
      hull.mastColor,
      "mast",
    );
    const mastCenterY = freeboard + m.height * 0.5;
    parts.push({
      mesh: mastMesh,
      transform: ([x, y, z]) => [x + m.offsetX, y + mastCenterY, z],
    });
  }

  // Keel fin: only render when there's no centerboard preset (avoid
  // double-drawing). Centerboard hulls render the swing-up blade below.
  if (!hull.anchors?.centerboardPivot) {
    const k = hull.keelFin;
    const finHeight = Math.max(0.05, k.depth - draft);
    const finMesh = createBox(
      k.chord,
      finHeight,
      k.thickness,
      hull.hullColor,
      "keelFin",
    );
    const finCenterY = -draft - finHeight * 0.5;
    parts.push({
      mesh: finMesh,
      transform: ([x, y, z]) => [x + k.offsetX, y + finCenterY, z],
    });
  }

  // Rudder: hangs below stern.
  {
    const r = hull.rudder;
    const rudMesh = createBox(r.chord, r.depth, 0.05, hull.mastColor, "rudder");
    const rudCenterY = -draft - r.depth * 0.5;
    parts.push({
      mesh: rudMesh,
      transform: ([x, y, z]) => [x + r.offsetX, y + rudCenterY, z],
    });
  }

  // ---- Anchor-driven hardware (Antila-style) ------------------------
  // Tiller, outboard engine, swing-up centerboard, transom panel.
  // All rendered only when the hull provides the matching anchors.
  if (hull.anchors) {
    const a = hull.anchors;
    if (a.tillerHandle) {
      for (const p of generateTiller(a)) parts.push(p);
    }
    if (a.engineMount) {
      for (const p of generateOutboardEngine(a)) parts.push(p);
    }
    if (a.centerboardPivot) {
      const ext = (hull as BoatHull & { _cbExtension?: number })._cbExtension;
      for (const p of generateCenterboard(a, hull, ext ?? 1)) parts.push(p);
    }
    for (const p of generateTransom(hull)) parts.push(p);
  }

  // ---- Cabin windows (two slim bands per side) -----------------------
  if (hull.cabin && showSuperstructure) {
    const c = hull.cabin;
    const winColor: [number, number, number] = [0.06, 0.08, 0.11];
    // Two horizontal slits per side, separated by a thin mullion.
    const winLen = c.length * 0.78;
    const bandHei = c.height * 0.18;
    const winThk = 0.02;
    const sideZ = c.width * 0.5 + winThk * 0.5 + 0.001;
    const upperY = freeboard + c.height * 0.72;
    const lowerY = freeboard + c.height * 0.45;
    for (const sign of [-1, +1] as const) {
      for (const cy of [upperY, lowerY]) {
        const win = createBox(winLen, bandHei, winThk, winColor, "cabinWindow");
        parts.push({
          mesh: win,
          transform: ([x, y, z]) => [x + c.offsetX, y + cy, z + sign * sideZ],
        });
      }
    }
    // Front "windscreen" panel: positioned on the raked front face.
    // For sailboats with anchors (Antila) the cabin front is raked back
    // by ~22°; place the windscreen at the X corresponding to the
    // window's Y so it sits flush with the bulkhead.
    {
      const isRaked =
        hull.spec.type === "sailboat" && hull.anchors !== undefined;
      const rakeAng = isRaked ? (22 * Math.PI) / 180 : 0;
      const tanRake = Math.tan(rakeAng);
      // Y above deck of windscreen centre.
      const screenY = upperY;
      const yAboveDeck = screenY - freeboard;
      const screenX =
        c.offsetX +
        c.length * 0.5 -
        tanRake * yAboveDeck +
        winThk * 0.5 +
        0.001;
      const front = createBox(
        winThk,
        bandHei * 1.1,
        c.width * 0.78,
        winColor,
        "cabinFront",
      );
      parts.push({
        mesh: front,
        transform: ([x, y, z]) => [x + screenX, y + screenY, z],
      });
    }
  }

  // ---- Sailboat deck dressing: pulpits + lifelines + swim platform ----
  if (hull.spec.type === "sailboat") {
    const railColor: [number, number, number] = [0.85, 0.85, 0.88];
    const deckY = freeboard;
    const stanchionHeight = 0.62;
    const railRadius = 0.018;
    // Approximate deck edge half-beam as function of station t (0..1, stern→bow).
    const sternHalfFrac = hull.spec.hullStyle === "modern-chine" ? 0.92 : 0.55;
    const bowHalfFrac = hull.spec.hullStyle === "modern-chine" ? 0.1 : 0.05;
    const halfBeamAt = (t: number): number => {
      // Smooth bell: peak ~midship, narrowing toward bow & stern.
      const s = Math.sin(Math.PI * t); // 0 at ends, 1 at mid
      const base = sternHalfFrac + (bowHalfFrac - sternHalfFrac) * t;
      return hull.beam * 0.5 * (base + (1 - Math.max(base, 0.5)) * s);
    };
    if (showRigging) {
      const xAt = (t: number) => -hull.length * 0.5 + t * hull.length * 0.96; // pull in slightly from ends
      const stanchionCount = Math.max(4, Math.round(hull.length / 1.4));
      type Pt = [number, number, number];
      const portTop: Pt[] = [];
      const stbdTop: Pt[] = [];
      for (let i = 0; i <= stanchionCount; i++) {
        const t = i / stanchionCount;
        const x = xAt(t);
        const z = halfBeamAt(t) * 0.96;
        // Stanchion: thin vertical box.
        for (const sign of [-1, +1] as const) {
          const st = createBox(
            0.04,
            stanchionHeight,
            0.04,
            railColor,
            "stanchion",
          );
          parts.push({
            mesh: st,
            transform: ([px, py, pz]) => [
              px + x,
              py + deckY + stanchionHeight * 0.5,
              pz + sign * z,
            ],
            shadowSkip: true,
          });
        }
        portTop.push([x, deckY + stanchionHeight, -z]);
        stbdTop.push([x, deckY + stanchionHeight, +z]);
      }
      // Horizontal rails: connect successive stanchion tops with thin rectangular tubes.
      const addRailSegment = (a: Pt, b: Pt, yOffset: number): void => {
        const dx = b[0] - a[0];
        const dz = b[2] - a[2];
        const len = Math.hypot(dx, dz);
        if (len < 1e-3) return;
        const cx = (a[0] + b[0]) * 0.5;
        const cy = (a[1] + b[1]) * 0.5 + yOffset;
        const cz = (a[2] + b[2]) * 0.5;
        const yaw = Math.atan2(dz, dx);
        const seg = createBox(
          len,
          railRadius * 2,
          railRadius * 2,
          railColor,
          "rail",
        );
        const cosY = Math.cos(yaw),
          sinY = Math.sin(yaw);
        parts.push({
          mesh: seg,
          transform: ([px, py, pz]) => [
            cx + px * cosY - pz * sinY,
            cy + py,
            cz + px * sinY + pz * cosY,
          ],
          shadowSkip: true,
        });
      };
      for (let i = 0; i < portTop.length - 1; i++) {
        // Top rail.
        addRailSegment(portTop[i]!, portTop[i + 1]!, 0);
        addRailSegment(stbdTop[i]!, stbdTop[i + 1]!, 0);
        // Mid rail (~30 cm below top).
        addRailSegment(portTop[i]!, portTop[i + 1]!, -stanchionHeight * 0.45);
        addRailSegment(stbdTop[i]!, stbdTop[i + 1]!, -stanchionHeight * 0.45);
      }
      // Bow pulpit closure: connect last port → last stbd at bow.
      const bowP = portTop[portTop.length - 1]!;
      const bowS = stbdTop[stbdTop.length - 1]!;
      addRailSegment(bowP, bowS, 0);
      addRailSegment(bowP, bowS, -stanchionHeight * 0.45);
      // Stern pushpit closure.
      addRailSegment(portTop[0]!, stbdTop[0]!, 0);
      addRailSegment(portTop[0]!, stbdTop[0]!, -stanchionHeight * 0.45);

      // Swim platform on modern-chine (Antila-style).
      if (hull.spec.hullStyle === "modern-chine") {
        const platLen = hull.length * 0.06;
        const platWid = hull.beam * 0.55;
        const plat = createBox(
          platLen,
          0.04,
          platWid,
          hull.deckColor,
          "swimPlatform",
        );
        parts.push({
          mesh: plat,
          transform: ([px, py, pz]) => [
            px - hull.length * 0.5 - platLen * 0.5 + 0.05,
            py + deckY * 0.55,
            pz,
          ],
        });
      }
    } // end showRigging

    // ---- Bimini (canvas dodger over cockpit) -------------------------
    // Skipped on hulls that opt out (Antila is sold without a fixed bimini).
    if (hull.cabin && showSuperstructure && !hull.anchors?.tillerHandle) {
      const c = hull.cabin;
      const biminiColor: [number, number, number] = [0.55, 0.58, 0.62];
      const biminiLen = hull.length * 0.18;
      const biminiWid = hull.beam * 0.78;
      const biminiThk = 0.04;
      const biminiY = deckY + stanchionHeight + 0.55;
      // Bimini sits aft of the cabin, over the cockpit.
      const biminiX = c.offsetX - c.length * 0.5 - biminiLen * 0.5 - 0.05;
      const bimini = createBox(
        biminiLen,
        biminiThk,
        biminiWid,
        biminiColor,
        "bimini",
      );
      parts.push({
        mesh: bimini,
        transform: ([x, y, z]) => [x + biminiX, y + biminiY, z],
      });
      // 4 frame posts (front-left, front-right, rear-left, rear-right).
      for (const sx of [-1, +1] as const) {
        for (const sz of [-1, +1] as const) {
          const post = createBox(
            0.03,
            biminiY - deckY,
            0.03,
            railColor,
            "biminiPost",
          );
          parts.push({
            mesh: post,
            transform: ([x, y, z]) => [
              x + biminiX + sx * biminiLen * 0.45,
              y + (biminiY + deckY) * 0.5,
              z + sz * biminiWid * 0.42,
            ],
          });
        }
      }
    }

    // ---- Fenders (white cylinders hanging on hull side) --------------
    if (showRigging) {
      const fenderColor: [number, number, number] = [0.96, 0.96, 0.96];
      const fenderLen = 0.42;
      const fenderDia = 0.12;
      const fenderY = deckY - 0.05 - fenderLen * 0.5;
      const hangers = hull.anchors?.fenderHangers;
      if (hangers && hangers.length > 0) {
        // Use anchor points for the X (along-hull) coordinate only — the
        // raw spec Z values are at the maximum beam (~midship), so applying
        // them verbatim near the bow leaves fenders hanging outside the
        // narrowing hull. Snap the lateral position to the actual deck
        // half-beam at each station instead.
        for (const h of hangers) {
          const fender = createBox(
            fenderDia,
            fenderLen,
            fenderDia,
            fenderColor,
            "fender",
          );
          const [hx, , hz] = h.position;
          const t = Math.max(
            0,
            Math.min(1, (hx + hull.length * 0.5) / hull.length),
          );
          const sideSign = hz >= 0 ? 1 : -1;
          const z = sideSign * (halfBeamAt(t) + fenderDia * 0.5 + 0.005);
          parts.push({
            mesh: fender,
            transform: ([px, py, pz]) => [px + hx, py + fenderY, pz + z],
          });
        }
      } else {
        // Legacy parametric fallback.
        const fenderCount = 3;
        for (let i = 0; i < fenderCount; i++) {
          const t = 0.2 + (i / (fenderCount - 1)) * 0.6;
          const x = -hull.length * 0.5 + t * hull.length;
          const z = halfBeamAt(t) + fenderDia * 0.5 + 0.005;
          for (const sign of [-1, +1] as const) {
            const fender = createBox(
              fenderDia,
              fenderLen,
              fenderDia,
              fenderColor,
              "fender",
            );
            parts.push({
              mesh: fender,
              transform: ([px, py, pz]) => [
                px + x,
                py + fenderY,
                pz + sign * z,
              ],
            });
          }
        }
      }
    } // end fenders

    // ---- Cleats (mooring fittings) ---------------------------------
    if (showRigging && hull.anchors?.cleats?.length) {
      const cleatParts = generateCleats(hull.anchors);
      for (const cp of cleatParts) parts.push(cp);
    }

    if (showRigging) {
      // ---- Lifebuoy (orange ring on stern pushpit) --------------------
      {
        const buoyColor: [number, number, number] = [0.95, 0.42, 0.16];
        // Approximated as 4 thin boxes forming a square ring.
        const buoyOuter = 0.32;
        const buoyTube = 0.06;
        const buoyY = deckY + stanchionHeight - 0.1;
        const buoyX = -hull.length * 0.5 + 0.25;
        const buoyZ = -hull.beam * 0.42; // hang on port side of pushpit
        // Two horizontal arms.
        for (const sy of [-1, +1] as const) {
          const arm = createBox(
            buoyTube,
            buoyTube,
            buoyOuter,
            buoyColor,
            "lifebuoyArm",
          );
          parts.push({
            mesh: arm,
            transform: ([px, py, pz]) => [
              px + buoyX,
              py + buoyY + sy * (buoyOuter * 0.5 - buoyTube * 0.5),
              pz + buoyZ,
            ],
          });
        }
        // Two vertical arms.
        for (const sz of [-1, +1] as const) {
          const arm = createBox(
            buoyTube,
            buoyOuter - buoyTube * 2,
            buoyTube,
            buoyColor,
            "lifebuoyArm",
          );
          parts.push({
            mesh: arm,
            transform: ([px, py, pz]) => [
              px + buoyX,
              py + buoyY,
              pz + buoyZ + sz * (buoyOuter * 0.5 - buoyTube * 0.5),
            ],
          });
        }
      }

      // ---- Boom (horizontal spar from mast aft) ------------------------
      {
        const m = hull.mast;
        const boomColor = hull.mastColor;
        const boomLen = hull.length * 0.42;
        const boomThk = 0.07;
        const boomY = deckY + 1.55;
        const boomX = m.offsetX - boomLen * 0.5;
        const boom = createBox(boomLen, boomThk, boomThk, boomColor, "boom");
        parts.push({
          mesh: boom,
          transform: ([x, y, z]) => [x + boomX, y + boomY, z],
        });
      }

      // ---- Standing rigging: forestay, backstay, shrouds (thin lines) --
      {
        const m = hull.mast;
        const stayColor: [number, number, number] = [0.78, 0.78, 0.82];
        const tellTaleColors: [number, number, number][] = [
          [1.0, 0.05, 0.04],
          [0.05, 1.0, 0.12],
        ];
        const mastTopX = m.offsetX;
        const mastTopY = deckY + m.height;
        const addStay = (
          from: [number, number, number],
          to: [number, number, number],
        ): void => {
          const dx = to[0] - from[0];
          const dy = to[1] - from[1];
          const dz = to[2] - from[2];
          const len = Math.hypot(dx, dy, dz);
          if (len < 0.1) return;
          const cx = (from[0] + to[0]) * 0.5;
          const cy = (from[1] + to[1]) * 0.5;
          const cz = (from[2] + to[2]) * 0.5;
          // Build a thin box of length=len along X, then rotate so X→direction.
          const stay = createBox(len, 0.02, 0.02, stayColor, "stay");
          // Rotation: yaw around Y to align horizontal projection, then pitch
          // around Z to lift to (dy, horizDist).
          const horiz = Math.hypot(dx, dz);
          const yaw = Math.atan2(dz, dx);
          const pitch = Math.atan2(dy, horiz);
          const cy2 = Math.cos(yaw),
            sy2 = Math.sin(yaw);
          const cp = Math.cos(pitch),
            sp = Math.sin(pitch);
          parts.push({
            mesh: stay,
            transform: ([px, py, pz]) => {
              // Apply pitch around Z first: (x, y) → (x·cp - y·sp, x·sp + y·cp)
              const x1 = px * cp - py * sp;
              const y1 = px * sp + py * cp;
              const z1 = pz;
              // Then yaw around Y: (x, z) → (x·cy - z·sy, x·sy + z·cy)
              const x2 = x1 * cy2 - z1 * sy2;
              const z2 = x1 * sy2 + z1 * cy2;
              return [cx + x2, cy + y1, cz + z2];
            },
            shadowSkip: true,
          });
        };
        const addTellTaleRibbon = (
          from: [number, number, number],
          to: [number, number, number],
          sideSign: -1 | 1,
        ): void => {
          const dx = to[0] - from[0];
          const dy = to[1] - from[1];
          const dz = to[2] - from[2];
          const len = Math.hypot(dx, dy, dz);
          if (len < 0.1) return;
          const ux = dx / len;
          const uy = dy / len;
          const uz = dz / len;
          // Lower point on the shroud for cockpit visibility.
          const t = 0.8;
          const ax = from[0] + dx * t;
          const ay = from[1] + dy * t;
          const az = from[2] + dz * t;
          const anchorX = ax + ux * 0.01;
          const anchorY = ay + uy * 0.01 - 0.05;
          const anchorZ = az + uz * 0.01 + sideSign * 0.018;

          const c = sideSign > 0 ? tellTaleColors[1]! : tellTaleColors[0]!;
          const ribbon = buildTellTaleRibbonMesh(
            sideSign,
            [anchorX, anchorY, anchorZ],
            c,
            runtime,
            "tellTale",
          );
          parts.push({
            mesh: ribbon,
            transform: (p) => p,
            shadowSkip: true,
          });
        };
        // Forestay → bow tip.
        addStay([mastTopX, mastTopY, 0], [hull.length * 0.48, deckY + 0.2, 0]);
        // Backstay → stern center.
        addStay([mastTopX, mastTopY, 0], [-hull.length * 0.48, deckY + 0.2, 0]);
        // Cap shrouds (port & starboard chainplates near midship).
        const shroudX = m.offsetX + hull.length * 0.04;
        const shroudZ = hull.beam * 0.42;
        const stbdShroudTo: [number, number, number] = [
          shroudX,
          deckY + 0.1,
          +shroudZ,
        ];
        const portShroudTo: [number, number, number] = [
          shroudX,
          deckY + 0.1,
          -shroudZ,
        ];
        const shroudFrom: [number, number, number] = [mastTopX, mastTopY, 0];
        addStay(shroudFrom, stbdShroudTo);
        addStay(shroudFrom, portShroudTo);
        addTellTaleRibbon(shroudFrom, stbdShroudTo, +1);
        addTellTaleRibbon(shroudFrom, portShroudTo, -1);
      }

      // ---- Steering wheel: only for hulls without a tiller -----------
      // Antila-class boats have a tiller (rendered above via generateTiller);
      // larger sailboats keep the cockpit wheel.
      if (!hull.anchors?.tillerHandle) {
        const wheelColor: [number, number, number] = [0.18, 0.18, 0.2];
        const wheelDia = 0.7;
        const wheelTube = 0.04;
        const wheelY = deckY + 0.55;
        const wheelX = -hull.length * 0.32;
        // Rim approximated by 8 short boxes arranged in a circle in YZ plane.
        const seg = 8;
        for (let i = 0; i < seg; i++) {
          const a0 = (i / seg) * Math.PI * 2;
          const a1 = ((i + 1) / seg) * Math.PI * 2;
          const z0 = Math.cos(a0) * wheelDia * 0.5;
          const y0 = Math.sin(a0) * wheelDia * 0.5;
          const z1 = Math.cos(a1) * wheelDia * 0.5;
          const y1 = Math.sin(a1) * wheelDia * 0.5;
          const cz = (z0 + z1) * 0.5;
          const cy = (y0 + y1) * 0.5;
          const segLen = Math.hypot(z1 - z0, y1 - y0);
          const ang = Math.atan2(y1 - y0, z1 - z0);
          const rim = createBox(
            wheelTube,
            wheelTube,
            segLen,
            wheelColor,
            "wheel",
          );
          const ca = Math.cos(ang),
            sa = Math.sin(ang);
          parts.push({
            mesh: rim,
            transform: ([px, py, pz]) => [
              px + wheelX,
              py + wheelY + cy + pz * sa,
              pz * ca - 0 + cz,
            ],
            shadowSkip: true,
          });
        }
        // Pedestal post.
        const post = createBox(
          0.1,
          wheelY - deckY,
          0.1,
          [0.72, 0.72, 0.74],
          "wheelPost",
        );
        parts.push({
          mesh: post,
          transform: ([x, y, z]) => [
            x + wheelX,
            y + (wheelY + deckY) * 0.5 - wheelDia * 0.4,
            z,
          ],
        });
      }
    } // end showRigging (lifebuoy+boom+stays+wheel)

    // ---- Waterline / chine accent stripe (dark band at WL) -----------
    {
      // Stripe is now baked into hullMesh via coveStripeColor in buildParentSailboatHull.
    }
  }

  return {
    mesh: mergeMeshes(parts, "boat"),
    shadowMesh: mergeMeshes(
      parts.filter((p) => !p.shadowSkip),
      "boat.shadow",
    ),
  };
}
